import React, { useState } from "react";
import { CssBaseline } from "@material-ui/core";
import { makeStyles, MuiThemeProvider } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import RefreshIcon from "@material-ui/icons/Refresh";
import Grid from "@material-ui/core/Grid";
import { SnackbarProvider } from "notistack";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import Header from "../header/Header";
import themes from "../../palette";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(0.75),
    paddingTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "calc(100vh - 64px)",
  },
  flexibleGrid: {
    display: "contents !important",
  },
  errorCard: {
    padding: theme.spacing(4),
    maxWidth: 500,
    textAlign: "center",
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  errorTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.error.main,
  },
  errorDescription: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  retryButton: {
    marginTop: theme.spacing(2),
  },
  helperText: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  success: { backgroundColor: "#2E7D32" },
  error: { backgroundColor: "#D10000" },
  warning: { backgroundColor: "#79791B" },
  info: { backgroundColor: "#651FFF" },
}));

function AccessDeniedPage({ fiscalYears, title, description, showRetry = false }) {
  // Get last active theme from localStorage
  const getLastActiveTheme = () => {
    let themeName = localStorage.getItem("themeName");
    if (themeName === "Dark Theme") {
      return themes.dark;
    } else {
      return themes.light;
    }
  };

  const [theme, setTheme] = useState(getLastActiveTheme());
  const [mobileOpen, setMobileOpen] = useState(false);
  const classes = useStyles();

  // Function that changes theme (same as App.jsx)
  const changeTheme = (themeName) => {
    if (themeName === "Dark Theme") {
      setTheme(themes.dark);
      localStorage.setItem("themeName", "Dark Theme");
    } else {
      setTheme(themes.blue);
      localStorage.setItem("themeName", "Default Theme");
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRetry = () => {
    window.location.reload();
  };

  const notistackRef = React.createRef();
  const onClickDismiss = (key) => () => {
    notistackRef.current.closeSnackbar(key);
  };

  return (
    <MuiThemeProvider theme={theme || themes.blue}>
      <SnackbarProvider
        ref={notistackRef}
        maxSnack={10}
        preventDuplicate
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        action={(key) => (
          <IconButton key="close" aria-label="close" color="inherit" onClick={onClickDismiss(key)}>
            <CloseIcon style={{ fontSize: "20" }} />
          </IconButton>
        )}
        classes={{
          variantSuccess: classes.success,
          variantError: classes.error,
          variantWarning: classes.warning,
          variantInfo: classes.info,
        }}
        autoHideDuration={4000}
      >
        <div className={classes.root}>
          <CssBaseline />
          <Grid container className={classes.flexibleGrid}>
            <Grid container className={classes.flexibleGrid}>
              {/* Reusing the actual Header component from the main app */}
              <Header
                onDrawerToggle={handleDrawerToggle}
                header=""
                isdrawerOpen={mobileOpen}
                setTheme={changeTheme}
                fiscalYears={fiscalYears}
                disableNotificationPopup={true}
              />
            </Grid>
            <Grid container className={classes.flexibleGrid}>
              <main className={classes.content}>
                <Paper className={classes.errorCard} elevation={3}>
                  <ErrorOutlineIcon className={classes.errorIcon} />
                  <Typography variant="h5" className={classes.errorTitle}>
                    {title}
                  </Typography>
                  <Typography variant="body1" className={classes.errorDescription}>
                    {description}
                  </Typography>
                  {showRetry && (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={handleRetry}
                        className={classes.retryButton}
                      >
                        Retry
                      </Button>
                      <Typography className={classes.helperText}>
                        Please Retry or select a different fiscal year from above and if the issue persists please contact support.
                      </Typography>
                    </>
                  )}
                  {!showRetry && (
                    <Typography className={classes.helperText}>
                      Select a different fiscal year from above and if the issue persists please contact support.
                    </Typography>
                  )}
                </Paper>
              </main>
            </Grid>
          </Grid>
        </div>
      </SnackbarProvider>
    </MuiThemeProvider>
  );
}

export default AccessDeniedPage;
